import type { CanonicalIncidentAssignmentRoleName, IncidentAssignment, IncidentAssignmentRoleName } from '../Types/IncidentAssignment';
import { getAppRoleLabel } from './appRoleLabels';

export const ECT_ROLE_NAMES: CanonicalIncidentAssignmentRoleName[] = [
  'ECTLead', 'Zone1', 'Zone2', 'Zone3', 'LabZone', 'AccountabilityCoordinator',
];

/** Shared ordering for page, drawer, filters and summaries. */
export const INCIDENT_TEAM_ROLES: CanonicalIncidentAssignmentRoleName[] = [
  'IncidentCommander', 'OfficeDirector', ...ECT_ROLE_NAMES, 'Headcounter', 'RecallTeam',
];

const ROLE_NUMBER_MAP: Record<number, CanonicalIncidentAssignmentRoleName> = {
  0: 'IncidentCommander', 1: 'Headcounter', 2: 'RecallTeam', 3: 'OfficeDirector',
  4: 'ECTLead', 5: 'Zone1', 6: 'Zone2', 7: 'Zone3', 8: 'LabZone', 9: 'AccountabilityCoordinator',
};

const ROLE_LABELS_PLURAL: Partial<Record<CanonicalIncidentAssignmentRoleName, string>> = {
  IncidentCommander: 'Incident Commanders', OfficeDirector: 'Office Directors',
  Headcounter: 'Headcounters', ECTLead: 'ECT Leads', AccountabilityCoordinator: 'Accountability Coordinators',
};

const ROLE_SUBTITLES: Record<CanonicalIncidentAssignmentRoleName, string> = {
  IncidentCommander: 'Primary incident leadership',
  OfficeDirector: 'Office-level incident oversight',
  Headcounter: 'People assigned to mark roster status',
  RecallTeam: 'Recall team support for headcount operations',
  ECTLead: 'Emergency coordination team leadership',
  Zone1: 'Emergency coordination for Zone 1',
  Zone2: 'Emergency coordination for Zone 2',
  Zone3: 'Emergency coordination for Zone 3',
  LabZone: 'Emergency coordination for the lab zone',
  AccountabilityCoordinator: 'Coordination of personnel accountability',
};

const ROLE_COLORS: Record<CanonicalIncidentAssignmentRoleName, string> = {
  IncidentCommander: '#1565C0', OfficeDirector: '#00695C', Headcounter: '#2E7D32', RecallTeam: '#7B1FA2',
  ECTLead: '#7C2D12', Zone1: '#1D4ED8', Zone2: '#0F766E', Zone3: '#6D28D9',
  LabZone: '#92400E', AccountabilityCoordinator: '#9D174D',
};

/** Normalize legacy value2 names before grouping, counting or filtering. */
export const resolveAssignmentRoleName = (
  roleName?: string, roleNumber?: number,
): CanonicalIncidentAssignmentRoleName | undefined => {
  if (roleName === 'BackupHeadcounter') return 'RecallTeam';
  if (roleName && INCIDENT_TEAM_ROLES.includes(roleName as CanonicalIncidentAssignmentRoleName)) {
    return roleName as CanonicalIncidentAssignmentRoleName;
  }
  return roleNumber === undefined ? undefined : ROLE_NUMBER_MAP[roleNumber];
};

export const isProminentEctRole = (roleName?: string, roleNumber?: number): boolean => {
  const role = resolveAssignmentRoleName(roleName, roleNumber);
  return role !== undefined && ECT_ROLE_NAMES.includes(role);
};

export const getAssignmentRoleColor = (roleName: IncidentAssignmentRoleName): string => {
  const role = resolveAssignmentRoleName(roleName);
  return role ? ROLE_COLORS[role] : '#4B5563';
};

export const getAssignmentRoleLabel = (roleName?: string, roleNumber?: number): string => {
  const role = resolveAssignmentRoleName(roleName, roleNumber);
  return role ? getAppRoleLabel(role) : roleName ?? 'Unknown Role';
};

export const getAssignmentRoleSortOrder = (roleName?: string, roleNumber?: number): number => {
  const role = resolveAssignmentRoleName(roleName, roleNumber);
  return role ? INCIDENT_TEAM_ROLES.indexOf(role) : 999;
};

export const groupIncidentAssignmentsByRole = (
  assignments: IncidentAssignment[],
): { roleName: CanonicalIncidentAssignmentRoleName; label: string; assignments: IncidentAssignment[] }[] => {
  const map = new Map<CanonicalIncidentAssignmentRoleName, IncidentAssignment[]>();
  for (const assignment of assignments) {
    const role = resolveAssignmentRoleName(assignment.assignmentRoleName, assignment.assignmentRole);
    if (!role) continue;
    if (!map.has(role)) map.set(role, []);
    map.get(role)!.push(assignment);
  }
  return INCIDENT_TEAM_ROLES.filter(role => map.has(role)).map(role => ({
    roleName: role, label: getAppRoleLabel(role), assignments: map.get(role)!,
  }));
};

export const pluralizeRoleLabel = (roleName: IncidentAssignmentRoleName, count: number): string => {
  const role = resolveAssignmentRoleName(roleName);
  if (!role) return getAssignmentRoleLabel(roleName);
  return count === 1 ? getAppRoleLabel(role) : ROLE_LABELS_PLURAL[role] ?? getAppRoleLabel(role);
};

export const formatTeamSummary = (assignments: IncidentAssignment[]): string => {
  const active = assignments.filter(assignment => assignment.isActive);
  if (active.length === 0) return 'No incident team assigned';
  return groupIncidentAssignmentsByRole(active)
    .map(group => `${group.assignments.length} ${pluralizeRoleLabel(group.roleName, group.assignments.length)}`)
    .join(' · ');
};

export const getRoleSubtitle = (roleName: IncidentAssignmentRoleName): string => {
  const role = resolveAssignmentRoleName(roleName);
  return role ? ROLE_SUBTITLES[role] : '';
};

/** Normalize names without changing assignment identities, evidence or count semantics. */
export const normalizeIncidentAssignments = (assignments: IncidentAssignment[]): IncidentAssignment[] =>
  assignments.map(assignment => {
    const role = resolveAssignmentRoleName(assignment.assignmentRoleName, assignment.assignmentRole);
    return role && role !== assignment.assignmentRoleName ? { ...assignment, assignmentRoleName: role } : assignment;
  });

/** Keep assignment counts stable if a roster join returns the same assignment twice. */
export const getUniqueActiveIncidentAssignments = (assignments: IncidentAssignment[]): IncidentAssignment[] => {
  const unique = new Map<string, IncidentAssignment>();
  for (const assignment of normalizeIncidentAssignments(assignments)) {
    if (!assignment.isActive) continue;
    const key = `${assignment.incidentId}:${assignment.incidentAssignmentId}`;
    const existing = unique.get(key);
    if (!existing || (assignment.hasIncidentDayBadgeActivity === true && existing.hasIncidentDayBadgeActivity !== true)) {
      unique.set(key, assignment);
    }
  }
  return Array.from(unique.values());
};
